import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { caseStudies } from './caseStudies';
import './App.css';

// ScrollToTop component - ページ遷移時に自動的にトップへスクロール
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-white text-slate-900">
        {/* Scroll Progress Bar */}
        <div
          className="fixed top-0 left-0 h-1 bg-blue-600 z-50 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 bg-[#1a2332] z-40 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src="/logo.png" alt="ベイビーステップ合同会社" className="h-8 w-8 sm:h-10 sm:w-10" />
              <span className="text-base sm:text-xl font-bold tracking-tight text-white">ベイビーステップ合同会社</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex gap-8 items-center">
              <Link to="/" className="text-white hover:text-blue-400 transition-colors font-medium">ホーム</Link>
              <Link to="/cases" className="text-white hover:text-blue-400 transition-colors font-medium">実績</Link>
              <Link to="/about" className="text-white hover:text-blue-400 transition-colors font-medium">会社概要</Link>
              <Link to="/contact" className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors font-medium">お問い合わせ</Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white hover:text-blue-400"
              aria-label="メニュー"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-[#1a2332] border-t border-slate-700">
              <nav className="flex flex-col px-4 py-2">
                <Link to="/" onClick={() => setMobileMenuOpen(false)} className="py-3 text-white hover:text-blue-400 transition-colors font-medium border-b border-slate-700">ホーム</Link>
                <Link to="/cases" onClick={() => setMobileMenuOpen(false)} className="py-3 text-white hover:text-blue-400 transition-colors font-medium border-b border-slate-700">実績</Link>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="py-3 text-white hover:text-blue-400 transition-colors font-medium border-b border-slate-700">会社概要</Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="my-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors font-medium text-center">お問い合わせ</Link>
              </nav>
            </div>
          )}
        </header>

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/cases" element={<CasesPage />} />
          <Route path="/cases/:id" element={<CaseDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-12 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img src="/logo.png" alt="ベイビーステップ合同会社" className="h-12 w-12" />
                  <h3 className="text-lg font-bold">ベイビーステップ合同会社</h3>
                </div>
                <p className="text-slate-400 text-sm">
                  お客様の成長に寄り添い、小さな一歩から大きな変革を共に実現するパートナーとして、最新技術で未来を創造します。
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-4">リンク</h4>
                <ul className="space-y-2 text-slate-400 text-sm">
                  <li><Link to="/" className="hover:text-white transition-colors">ホーム</Link></li>
                  <li><Link to="/cases" className="hover:text-white transition-colors">実績</Link></li>
                  <li><Link to="/about" className="hover:text-white transition-colors">会社概要</Link></li>
                  <li><Link to="/contact" className="hover:text-white transition-colors">お問い合わせ</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">お問い合わせ</h4>
                <p className="text-slate-400 text-sm">
                  ご質問やご相談は<br />
                  お気軽にお問い合わせください
                </p>
                <Link to="/contact" className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium">
                  お問い合わせフォーム
                </Link>
              </div>
            </div>
            <div className="border-t border-slate-800 pt-8 text-center">
              <p className="text-slate-400">© 2025 ベイビーステップ合同会社. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

// HomePage Component
function HomePage() {
  const navigate = useNavigate();
  const [selectedStat, setSelectedStat] = useState(null);

  const strengths = [
    { num: '01', title: '圧倒的なスピード', desc: 'AI技術を駆使し、従来の開発期間を大幅に短縮。提案段階で動作するプロトタイプを提供し、具体的なイメージを即座に共有できます。' },
    { num: '02', title: '高精度な要件把握', desc: '動くプロトタイプで早期にフィードバックを得ることで、イメージのズレを最小限に抑え、手戻りのない開発を実現します。' },
    { num: '03', title: '最新AI技術の活用', desc: '生成AI、機械学習など最先端技術を積極的に導入。自動化可能な作業を最大限効率化し、クリエイティブな部分に注力します。' },
    { num: '04', title: 'アジャイルな開発体制', desc: '小さなイテレーションで価値を提供し続けます。市場の変化や新たな要望にも柔軟かつ迅速に対応可能です。' }
  ];

  const process = [
    { num: '01', title: 'ヒアリング', desc: 'お客様のビジョンを深く理解し、AIを活用した最適なソリューションを提案' },
    { num: '02', title: 'プロトタイプ開発', desc: '他社が提案書を作成している間に、動作するプロトタイプを提供' },
    { num: '03', title: 'フィードバック&改善', desc: '実際の動作を確認しながら、細かな調整と機能追加を高速で実施' },
    { num: '04', title: '本格開発&リリース', desc: 'プロトタイプをベースに本格開発。既に方向性が定まっているため高速' }
  ];

  return (
    <div className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/hero-background.mp4" type="video/mp4" />
        </video>

        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-slate-900/60"></div>

        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12 sm:py-32 md:py-40 relative z-10 w-full">
          <div className="max-w-3xl">
            <p className="text-white font-medium tracking-[0.3em] uppercase text-sm mb-6" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>Rapid Prototyping</p>
            <h1 className="font-bold mb-8 tracking-tight text-white hero-title">
              提案書より先に<br />
              動くプロトタイプを
            </h1>
            <p className="text-lg sm:text-xl text-white/90 font-light leading-relaxed mb-10" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
              最新AI技術で顧客のイメージを最速で形にする<br />
              次世代の受託開発パートナー
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 bg-blue-600 text-white rounded font-medium text-base transition-all duration-300 hover:bg-blue-700 hover:shadow-lg hover:scale-105 active:scale-95"
              >
                無料相談を予約 →
              </button>
              <button
                onClick={() => navigate('/cases')}
                className="px-8 py-4 bg-white text-slate-900 rounded font-medium text-base transition-all duration-300 hover:bg-slate-100 hover:shadow-lg hover:scale-105 active:scale-95"
              >
                実績を見る
              </button>
            </div>
          </div>
        </div>

        {/* Decorative Line */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-px bg-slate-300"></div>
            <div className="w-2 h-2 rounded-full bg-blue-600"></div>
            <div className="w-16 h-px bg-slate-300"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                value: '30%',
                label: '開発期間短縮',
                title: '開発期間短縮について',
                description: '事前にプロトタイプを作成することで、お客様との認識のずれを極限まで減らし、手戻りを最小限に抑えます。\n\nその結果、最大30%の期間短縮に成功しています。\n\n例えば、他社では平均6ヶ月かかるプロジェクトを4ヶ月弱で納品した実績があります。'
              },
              {
                value: '95%',
                label: '顧客満足度',
                title: '顧客満足度について',
                description: 'プロトタイプを先に見ていただくことで、開発前のイメージ通りのプロダクトを実現できます。\n\nお客様が最初に思い描いていた通りの成果物をお届けできるため、95%以上のお客様にご満足いただいています。'
              },
              {
                value: '98%',
                label: 'プロジェクト成功率',
                title: 'プロジェクト成功率について',
                description: '当社では、納期の遅れが発生せずに納品できることをプロジェクト成功と定義しています。\n\n事前にプロトタイプで方向性を確認するため、手戻りが少なく、98%のプロジェクトで納期通りの納品を実現しています。'
              }
            ].map((stat, index) => (
              <div
                key={index}
                onClick={() => setSelectedStat(stat)}
                className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
                style={{
                  backdropFilter: 'blur(10px)',
                  background: 'rgba(255, 255, 255, 0.7)'
                }}
              >
                <div className="text-5xl font-bold text-blue-600 mb-2 group-hover:scale-110 transition-transform duration-300">{stat.value}</div>
                <div className="text-slate-600 font-medium tracking-wide">{stat.label}</div>
                <div className="mt-4 w-12 h-1 bg-blue-600 group-hover:w-full transition-all duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Modal */}
      {selectedStat && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedStat(null)}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedStat(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="text-5xl font-bold text-blue-600 mb-4">{selectedStat.value}</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{selectedStat.title}</h3>
            <div className="text-gray-600 leading-relaxed whitespace-pre-line">{selectedStat.description}</div>
          </div>
        </div>
      )}


      {/* Decorative Divider */}
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-4">
          <div className="w-24 h-px bg-slate-300"></div>
          <div className="w-3 h-3 rounded-full bg-blue-600"></div>
          <div className="w-24 h-px bg-slate-300"></div>
        </div>
      </div>

      {/* Strengths Section */}
      <section className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">Our Strengths</p>
            <h2 className="text-4xl font-bold tracking-tight">私たちの強み</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {strengths.map((item, index) => (
              <div key={index} className="group p-8 bg-white rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="text-6xl font-bold text-blue-600/20 group-hover:text-blue-600/40 transition-colors duration-300">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3 tracking-tight">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed font-light">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">Development Process</p>
            <h2 className="text-4xl font-bold tracking-tight">開発プロセス</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <div key={index} className="group relative p-6 bg-white rounded-xl border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300">
                <div className="text-4xl font-bold text-blue-600 mb-4">{step.num}</div>
                <h3 className="text-xl font-bold mb-3 tracking-tight">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed font-light">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}

      <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            まずは動くものを<br />見てください
          </h2>
          <p className="text-xl text-slate-300 mb-12 font-light leading-relaxed">
            打ち合わせでご要望をヒアリング後<br />
            最短翌日でプロトタイプを提供します
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="group relative px-12 py-5 bg-white text-slate-900 rounded-lg overflow-hidden font-bold text-lg"
          >
            <span className="relative z-10 group-hover:text-white transition-colors duration-300">無料相談を予約する</span>
            <div className="absolute inset-0 bg-blue-600 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
          </button>
        </div>
      </section>

    </div>
  );
}

export default App;

// CasesPage Component
function CasesPage() {
  const navigate = useNavigate();

  const stats = [
    { value: '30%', label: '期間短縮', description: '平均開発期間短縮率' },
    { value: '24h', label: '初回提案', description: '最速プロトタイプ提供' },
    { value: '1.5倍', label: 'アプリ継続利用ユーザー数増加', description: 'アプリの継続利用率向上' },
    { value: '20%以上', label: '業務効率', description: '各顧客において平均20%以上の業務効率化に成功' }
  ];

  const categories = [
    {
      title: 'AI/機械学習システム',
      description: '需要予測、画像認識、自然言語処理など、最先端のAI技術を活用したシステム開発。ビジネス課題を自動化・最適化します。'
    },
    {
      title: 'ECプラットフォーム',
      description: '在庫管理、顧客管理、決済システムなど、EC事業に必要な機能を統合したプラットフォームを構築します。'
    },
    {
      title: 'SaaS/業務システム',
      description: '企業の業務効率化を実現するカスタムSaaSの開発。クラウドネイティブな設計で拡張性と保守性を確保します。'
    },
    {
      title: 'データ分析基盤',
      description: 'ビッグデータの収集・蓄積・可視化まで。経営判断をサポートするBI基盤を構築します。'
    }
  ];

  return (
    <div className="pt-20 min-h-screen">
      {/* Hero Section - ダークグラデーション背景 */}
      <section className="relative min-h-[60vh] flex items-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>

        <div className="max-w-7xl mx-auto px-6 py-24 relative z-10 w-full">
          <div className="text-center text-white">
            <p className="text-blue-400 font-medium tracking-[0.3em] uppercase text-sm mb-6">Case Studies</p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8">実績紹介</h1>
            <p className="text-xl md:text-2xl text-slate-300 font-light max-w-3xl mx-auto leading-relaxed">
              お客様の課題を解決し、ビジネスを加速させた事例をご紹介します
            </p>
          </div>
        </div>

        {/* Decorative Line */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-px bg-slate-400"></div>
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <div className="w-16 h-px bg-slate-400"></div>
          </div>
        </div>
      </section>

      {/* Stats Section - 統計数字 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">Our Achievements</p>
            <h2 className="text-4xl font-bold tracking-tight mb-4">数字で見る実績</h2>
            <p className="text-slate-600 text-lg font-light">確かな実績が、私たちの信頼の証です</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-slate-50 border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3 group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-slate-900 font-bold text-lg mb-2">{stat.label}</div>
                <div className="text-slate-600 text-sm font-light">{stat.description}</div>
                <div className="mt-4 w-12 h-1 bg-blue-600 group-hover:w-full transition-all duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section - 対応領域 */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">Our Expertise</p>
            <h2 className="text-4xl font-bold tracking-tight mb-4">対応領域</h2>
            <p className="text-slate-600 text-lg font-light max-w-2xl mx-auto">
              多様な業界、技術領域での開発実績により、様々なお客様の課題に対応しています
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {categories.map((category, index) => (
              <div
                key={index}
                className="group p-8 bg-white rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-6">
                  <div className="text-5xl font-bold text-blue-600/20 group-hover:text-blue-600/40 transition-colors duration-300">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3 tracking-tight group-hover:text-blue-600 transition-colors">
                      {category.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed font-light">
                      {category.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section - 事例一覧 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">Success Stories</p>
            <h2 className="text-4xl font-bold tracking-tight mb-4">導入事例</h2>
            <p className="text-slate-600 text-lg font-light">実際のプロジェクト事例をご覧ください</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((caseStudy, index) => (
              <div
                key={index}
                onClick={() => navigate(`/cases/${caseStudy.id}`)}
                className="group cursor-pointer bg-white rounded-2xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2"
              >
                <div className="aspect-video bg-gradient-to-br from-blue-100 to-slate-100 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-blue-600 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
                  <img
                    src={caseStudy.image}
                    alt={caseStudy.title}
                    className="w-32 h-32 object-contain opacity-60 group-hover:opacity-100 group-hover:scale-125 transition-all duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-bold text-blue-600 px-3 py-1 bg-blue-50 rounded-full tracking-wide uppercase">
                      {caseStudy.category}
                    </div>
                    <span className="text-blue-600 group-hover:translate-x-2 transition-transform duration-300 text-xl">→</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 tracking-tight group-hover:text-blue-600 transition-colors line-clamp-2">
                    {caseStudy.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 font-light">{caseStudy.client}</p>
                  <div className="flex items-center text-xs text-slate-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    開発期間: {caseStudy.duration}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            同じような課題を<br />お持ちではありませんか？
          </h2>
          <p className="text-xl text-slate-300 mb-12 font-light leading-relaxed">
            まずはお気軽にご相談ください<br />
            最短翌日でプロトタイプをご提供します
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="group relative px-10 py-5 bg-white text-slate-900 rounded-lg overflow-hidden font-bold text-lg shadow-xl hover:shadow-2xl transition-all"
            >
              <span className="relative z-10 group-hover:text-white transition-colors duration-300">無料相談を予約する</span>
              <div className="absolute inset-0 bg-blue-600 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"></div>
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-10 py-5 border-2 border-white text-white rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-300 font-bold text-lg"
            >
              サービス詳細を見る
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// CaseDetailPage Component
function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const caseStudy = caseStudies.find(c => c.id === parseInt(id));

  if (!caseStudy) {
    return (
      <div className="pt-32 pb-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">事例が見つかりません</h1>
          <button onClick={() => navigate('/cases')} className="text-blue-600 hover:underline">
            実績一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-5xl mx-auto px-6">
        {/* Breadcrumb */}
        <div className="mb-8 text-sm text-slate-600">
          <Link to="/" className="hover:text-blue-600">ホーム</Link>
          <span className="mx-2">/</span>
          <Link to="/cases" className="hover:text-blue-600">実績</Link>
          <span className="mx-2">/</span>
          <span>{caseStudy.title}</span>
        </div>

        {/* Header */}
        <div className="mb-12">
          <div className="text-xs font-medium text-blue-600 mb-3 tracking-wide uppercase">{caseStudy.category}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">{caseStudy.title}</h1>
          <p className="text-xl text-slate-600 font-light">{caseStudy.client}</p>
          <div className="flex gap-6 mt-6 text-sm text-slate-600">
            <div><span className="font-medium">開発期間:</span> {caseStudy.duration}</div>
          </div>
        </div>

        {/* Main Image */}
        <div className="aspect-video bg-gradient-to-br from-blue-100 to-slate-100 rounded-2xl mb-12 flex items-center justify-center">
          <img src={caseStudy.image} alt={caseStudy.title} className="w-48 h-48 object-contain opacity-60" />
        </div>

        {/* Challenge */}

        <section className="mb-12 p-8 bg-white rounded-2xl border border-slate-200">
          <div className="flex items-start gap-6">
            <div className="text-5xl font-bold text-red-500/20">01</div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-4 tracking-tight text-red-600">{caseStudy.challenge.title}</h2>
              <p className="text-slate-700 leading-relaxed text-lg font-light">{caseStudy.challenge.description}</p>
            </div>
          </div>
        </section>


        {/* Approach */}

        <section className="mb-12 p-8 bg-white rounded-2xl border border-slate-200">
          <div className="flex items-start gap-6">
            <div className="text-5xl font-bold text-blue-500/20">02</div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-4 tracking-tight text-blue-600">{caseStudy.approach.title}</h2>
              <p className="text-slate-700 leading-relaxed text-lg font-light">{caseStudy.approach.description}</p>
            </div>
          </div>
        </section>


        {/* Solution */}

        <section className="mb-12 p-8 bg-white rounded-2xl border border-slate-200">
          <div className="flex items-start gap-6">
            <div className="text-5xl font-bold text-green-500/20">03</div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-4 tracking-tight text-green-600">{caseStudy.solution.title}</h2>
              <p className="text-slate-700 leading-relaxed text-lg font-light mb-6">{caseStudy.solution.description}</p>

              {/* Results */}
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {caseStudy.results.map((result, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-green-600"></div>
                    <span className="font-medium text-slate-800">{result}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* Technologies */}

        <section className="mb-12 p-8 bg-slate-50 rounded-2xl">
          <h3 className="text-2xl font-bold mb-6 tracking-tight">使用技術</h3>
          <div className="flex flex-wrap gap-3">
            {caseStudy.technologies.map((tech, index) => (
              <span key={index} className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700">
                {tech}
              </span>
            ))}
          </div>
        </section>


        {/* CTA */}
        <div className="text-center mt-16 p-12 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl">
          <h3 className="text-2xl font-bold mb-4 tracking-tight">同じような課題をお持ちですか?</h3>
          <p className="text-slate-600 mb-6 font-light">まずはお気軽にご相談ください</p>
          <button
            onClick={() => navigate('/contact')}
            className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            無料相談を予約する
          </button>
        </div>

        {/* Back Button */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/cases')}
            className="text-slate-600 hover:text-blue-600 transition-colors font-medium"
          >
            ← 実績一覧に戻る
          </button>
        </div>
      </div>
    </div>
  );
}

// AboutPage Component
function AboutPage() {
  return (
    <div className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">About Us</p>
          <h1 className="text-5xl font-bold tracking-tight mb-6">会社概要</h1>
        </div>


        <div className="bg-white rounded-2xl border border-slate-200 p-12 mb-12">
          <div className="flex items-center justify-center mb-12">
            <img src="/logo.png" alt="ベイビーステップ合同会社" className="w-32 h-32" />
          </div>

          <table className="w-full">
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="py-4 pr-8 font-medium text-slate-600 w-1/3">会社名</td>
                <td className="py-4 text-slate-900">ベイビーステップ合同会社</td>
              </tr>
              <tr>
                <td className="py-4 pr-8 font-medium text-slate-600">代表者</td>
                <td className="py-4 text-slate-900">江角 宥熙</td>
              </tr>
              <tr>
                <td className="py-4 pr-8 font-medium text-slate-600">事業内容</td>
                <td className="py-4 text-slate-900">
                  AI技術を活用したソフトウェア開発<br />
                  ラピッドプロトタイピングサービス<br />
                  DX推進コンサルティング
                </td>
              </tr>
            </tbody>
          </table>
        </div>



        <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-6 tracking-tight text-center">代表挨拶</h2>
          <p className="text-lg text-slate-700 leading-relaxed font-light text-center mb-8">
            お客様の成長に寄り添い、小さな一歩から大きな変革を共に実現するパートナーとして、最新技術で未来を創造します。
          </p>
          <div className="text-center">
            <p className="text-slate-600 font-medium">代表 江角 宥熙</p>
          </div>
        </div>



        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { title: 'スピード', desc: '提案書より先に動くプロトタイプを提供し、お客様の意思決定を加速します' },
            { title: '品質', desc: '最新のAI技術と確かな開発力で、高品質なソリューションを実現します' },
            { title: 'パートナーシップ', desc: 'お客様と共に成長し、長期的な信頼関係を築きます' }
          ].map((item, index) => (
            <div key={index} className="p-6 bg-white rounded-xl border border-slate-200 text-center">
              <h3 className="text-xl font-bold mb-3 tracking-tight">{item.title}</h3>
              <p className="text-sm text-slate-600 font-light leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

// ContactPage Component
function ContactPage() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('https://formspree.io/f/mnnlqkal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (submitted) {
    return (
      <div className="pt-32 pb-20 min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white rounded-2xl border border-slate-200 p-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold mb-4 tracking-tight">送信完了</h2>
            <p className="text-slate-600 mb-8 font-light">
              お問い合わせありがとうございます。<br />
              担当者より2営業日以内にご連絡させていただきます。
            </p>
            <Link to="/" className="inline-block px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
              ホームに戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20 min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-blue-600 font-medium tracking-[0.3em] uppercase text-sm mb-4">Contact</p>
          <h1 className="text-5xl font-bold tracking-tight mb-6">お問い合わせ</h1>
          <p className="text-xl text-slate-600 font-light">
            ご質問やご相談など、お気軽にお問い合わせください
          </p>
        </div>


        <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                会社名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                required
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="株式会社サンプル"
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                お名前 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="山田 太郎"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="example@company.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                電話番号
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="03-1234-5678"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                placeholder="お問い合わせ内容をご記入ください"
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="w-full py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg"
              >
                送信する
              </button>
            </div>
          </form>
        </div>


        <div className="mt-12 text-center text-sm text-slate-600">
          <p>お急ぎの場合は、お電話でもお問い合わせいただけます</p>
        </div>
      </div>
    </div>
  );
}

